import { useState } from "react";
import PageContainer from "~/components/common/PageContainer";
import { DealFeed } from "~/components/common/DealFeed";
import { cn } from "~/lib/utils";
import { useSubsidyDealsStore } from "~/lib/stores/trending-deals";
import { buildSubsidyChannel } from "~/lib/deal-channel";
import { useFetchOnce } from "~/lib/hooks/useFetchOnce";
import { FaArrowLeft } from "react-icons/fa";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "~/components/ui/breadcrumb";
import { Link, useNavigate } from "react-router";
import Footer from "~/components/common/Footer";
import type { GoogleCategoryTree } from "~/api/deal";
import subsidyBannerImg from "~/assets/deal-feed/subsidy-program.svg";

export default function SubsidyProgram() {
  const navigate = useNavigate();
  const { categories, isCategoriesLoading, fetchCategories } = useSubsidyDealsStore();
  const [category, setCategory] = useState<GoogleCategoryTree | null>(null);

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/shop");
    }
  };

  useFetchOnce(
    "subsidy-categories",
    () => {
      fetchCategories();
    },
    !isCategoriesLoading
  );

  const showCategory = category || categories[0];
  const showCategories = categories.slice(0, 10);

  return (
    <PageContainer>
      <div className="flex items-center justify-center">
        <div
          className={cn(
            "my-[24px] w-full max-w-[1408px] px-[16px]",
            "flex flex-col items-center justify-center gap-[24px]"
          )}
        >
          <div id="menu" className="flex h-[24px] w-full items-center gap-[8px]">
            <FaArrowLeft onClick={goBack} />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>$10B Subsidy Program</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          <div className="flex w-full flex-col items-center justify-center gap-[24px] rounded-[24px] bg-white p-[24px]">
            <SubsidyBanner />
            <SubsidyTabs value={showCategory} categories={showCategories} onCategoryChange={setCategory} />
            <SubsidyDealFeed categoryId={category?.categoryId} />
          </div>
        </div>
      </div>
      <Footer />
    </PageContainer>
  );
}

function SubsidyTabs({
  value,
  categories,
  onCategoryChange,
}: {
  value: GoogleCategoryTree | null;
  categories: GoogleCategoryTree[];
  onCategoryChange: (category: GoogleCategoryTree | null) => void;
}) {
  return (
    <div className="flex flex-row flex-wrap items-center justify-center gap-[8px]">
      {categories.map(category => (
        <div key={category.categoryId} className="basis-auto pl-3">
          <button
            id={`subsidy-program-category-${category.categoryId}`}
            onClick={() => onCategoryChange(category)}
            className={cn(
              "flex-shrink-0",
              "flex items-center justify-center",
              "h-[32px] rounded-[12px] border px-[16px] py-[8px]",
              "transition-all duration-200",
              "text-[14px] leading-[20px] font-medium text-[#171717]",
              value?.categoryId === category.categoryId
                ? "border-[#0000000D] bg-[#FFFEDD] font-bold"
                : "border-[#0000001A] bg-white font-medium"
            )}
          >
            {category.category}
          </button>
        </div>
      ))}
    </div>
  );
}

function SubsidyDealFeed({ categoryId }: { categoryId?: string }) {
  const { deals, isLoading, fetchDeals, fetchMoreDeals } = useSubsidyDealsStore();

  useFetchOnce(
    categoryId ?? "",
    () => {
      const channel = buildSubsidyChannel(categoryId);
      fetchDeals(categoryId, channel);
    },
    !isLoading
  );

  return (
    <DealFeed
      deals={deals}
      isLoading={isLoading}
      hasMore={true}
      onLoadMore={() => {
        const channel = buildSubsidyChannel(categoryId);
        fetchMoreDeals(categoryId, channel);
      }}
      className="my-[16px] w-full"
      dealPerRow={{
        default: 2,
        mobile: 2,
        tablet: 4,
        desktopSmall: 5,
        desktopLarge: 5,
      }}
    />
  );
}

function SubsidyBanner() {
  return (
    <div
      className="relative h-56 w-full max-w-[1440px] overflow-hidden rounded-[32px]"
      style={{
        background: "linear-gradient(180deg, #ABE5FF 0%, #EEFAFF 100%)",
      }}
    >
      <div className="absolute inset-0 z-1 flex h-full w-full items-center justify-center">
        <img src={subsidyBannerImg} alt="Subsidy Program" className="h-full w-auto flex-shrink-0" />
      </div>
      <div className="absolute inset-0 z-2 flex h-full w-full flex-col items-center justify-center gap-1 text-center">
        <div className="text-xl leading-normal font-semibold text-neutral-900/50">Best deals on premium brands</div>
        <div className="text-5xl leading-[52px] font-bold text-neutral-900">$10B Subsidy Program</div>
      </div>
    </div>
  );
}
